import { Callback } from '../../../modules/callback'
import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { IFlowSubscriber } from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { unescapeHtml } from '../../../modules/utils'

// ─────────────────────────────────────────────────────────────────────────────
// Constants & Types
// ─────────────────────────────────────────────────────────────────────────────

const CONTRIBUTOR_FIELDS = [
  'nameFr',
  'nameEn',
  'contributorType',
  'alternateName',
  'descriptionFr',
  'descriptionEn',
  'mainEntityOfPageFr',
  'mainEntityOfPageEn',
  'nationality',
  'birthplace',
  'workplace',
  'postalAddress',
  'wikidataId',
  'isni',
  'shopifyUrl',
  'appleUrl',
  'youtubeUrl',
] as const

type ContributorField = (typeof CONTRIBUTOR_FIELDS)[number]

// Mapbox address autofill provides structured address fields
export interface ContributorAddress {
  streetAddress?: string // from 'street-address'
  addressLine2?: string // from 'address-line2'
  addressLocality?: string // from 'address-level2' (city)
  addressRegion?: string // from 'address-level1' (province)
  postalCode?: string // from 'postal-code'
}

// ─────────────────────────────────────────────────────────────────────────────
// Localization
// ─────────────────────────────────────────────────────────────────────────────

Locale.getInstance().registerKeySet('fr', 'create-contributor-info', {
  title: 'Soumettre un nouveau contributeur',
  description: 'Vous pouvez soumettre un nouveau contributeur.',
  nameFr: 'Nom du contributeur français*',
  nameEn: 'Nom du contributeur anglais*',
  contributorType: 'Type de contributeur*',
  person: 'Personne',
  organization: 'Organisation',
  alternateName: 'Nom alternatif',
  descriptionFr: 'Description française*',
  descriptionEn: 'Description anglaise',
  mainEntityOfPageFr: 'URL français',
  mainEntityOfPageEn: 'URL anglais',
  nationality: 'Nationalité',
  birthplace: "Ville d'origine",
  workplace: 'Lieu de travail',
  postalAddress: 'Adresse postale',
  wikidataId: 'ID Wikidata (ex: Q293229)',
  isni: 'ISNI (ex: 0000000078253261)',
  shopifyUrl: 'Lien Shopify',
  appleUrl: 'Lien Apple',
  youtubeUrl: 'Lien YouTube',
  placeholder: 'Écrivez ici...',
  placeholderNationality: 'Ex: Canada, France, États-Unis...',
  placeholderBirthplace: 'Ex: Sherbrooke, Québec',
  placeholderWorkplace: 'Ex: Montréal, Québec',
  personalInfo: 'Informations personnelles',
  orgInfo: 'Informations organisation',
  linksAndIds: 'Liens et identifiants',
  errors: {
    nameFr: 'Veuillez entrer le nom du contributeur en français',
    nameEn: 'Veuillez entrer le nom du contributeur en anglais',
    contributorType: 'Veuillez choisir un type de contributeur',
    descriptionFr: 'Veuillez entrer la description en français',
    generic: 'Veuillez corriger les erreurs ci-dessous',
  },
})

Locale.getInstance().registerKeySet('en', 'create-contributor-info', {
  title: 'Submit a new contributor',
  description: 'You can submit a new contributor.',
  nameFr: 'Contributor name - French*',
  nameEn: 'Contributor name - English*',
  contributorType: 'Contributor type*',
  person: 'Person',
  organization: 'Organization',
  alternateName: 'Alternate name',
  descriptionFr: 'Description - French*',
  descriptionEn: 'Description - English',
  mainEntityOfPageFr: 'URL - French',
  mainEntityOfPageEn: 'URL - English',
  nationality: 'Nationality',
  birthplace: 'City of origin',
  workplace: 'Workplace',
  postalAddress: 'Postal address',
  wikidataId: 'Wikidata ID (ex: Q293229)',
  isni: 'ISNI (ex: 0000000078253261)',
  shopifyUrl: 'Shopify link',
  appleUrl: 'Apple link',
  youtubeUrl: 'YouTube link',
  placeholder: 'Write here...',
  placeholderNationality: 'Ex: Canada, France, United States...',
  placeholderBirthplace: 'Ex: Sherbrooke, Quebec',
  placeholderWorkplace: 'Ex: Montreal, Quebec',
  personalInfo: 'Personal Information',
  orgInfo: 'Organization Information',
  linksAndIds: 'Links and Identifiers',
  errors: {
    nameFr: 'Please enter the contributor name in French',
    nameEn: 'Please enter the contributor name in English',
    contributorType: 'Please select a contributor type',
    descriptionFr: 'Please enter the description in French',
    generic: 'Please correct the errors below',
  },
})

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────

export type ContributorInfoData = Record<ContributorField, string>

export interface LvcCreateContributorInfo extends Record<ContributorField, string> {
  flowGroup: string
  flowStep: string
  errors: Record<string, string | false>
  contributorInfoData: ContributorInfoData
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export class LvcCreateContributorInfo
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private changeInputCb = Callback.attach(this.onInputChange.bind(this))
  private onChangeAddressAutocompCb = Callback.attach(this.onChangeAddressAutocomp.bind(this))

  reactive: Reactive = [
    // Generate reactive fields dynamically from CONTRIBUTOR_FIELDS
    ...CONTRIBUTOR_FIELDS.map((field): [string, string, boolean] => [field, '', false]),
    [
      'errors',
      {
        nameFr: false,
        nameEn: false,
        contributorType: false,
        descriptionFr: false,
        generic: false,
      },
      false,
    ],
    ['contributorInfoData', {} as ContributorInfoData, false],
    ['postalAddressData', {} as ContributorAddress, false],
  ]

  attrs: Attrs = [
    ['flow-group', ''],
    ['flow-step', ''],
  ]

  // ─────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────────────────

  onReady() {
    // Initialize postalAddressData with default empty string values
    this.postalAddressData = {
      streetAddress: '',
      addressLine2: '',
      addressLocality: '',
      addressRegion: '',
      postalCode: '',
    }
    MainModule.getInstance()?.getFlowManager()?.subscribe(this)
  }

  onDestroy() {
    MainModule.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Event Handlers
  // ─────────────────────────────────────────────────────────────────────────

  private onContributorChangeEvent = (e: CustomEvent) => {
    const contributorTypeDetail = e.detail
    if (!contributorTypeDetail) return

    this.contributorType = contributorTypeDetail
    this.updateContributorInfoData()
    this.saveToFlowCore()
  }

  private onInputChange() {
    // Collect values from all input fields
    for (const field of CONTRIBUTOR_FIELDS) {
      if (field === 'contributorType') continue // Handled separately via event
      const element = this.select(`[name="${field}"]`) as HTMLInputElement | null
      ;(this as any)[field] = element?.value || ''
    }

    this.updateContributorInfoData()
    this.saveToFlowCore()
  }

  private onChangeAddressAutocomp(data: any) {
    // Store structured address data for later serialization
    this.postalAddressData = {
      streetAddress: data['street-address'] || '',
      addressLine2: data['address-line2'] || '',
      addressLocality: data['address-level2'] || '',
      addressRegion: data['address-level1'] || '',
      postalCode: data['postal-code'] || '',
    }
    this.saveToFlowCore()
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Helper Methods
  // ─────────────────────────────────────────────────────────────────────────

  private updateContributorInfoData() {
    this.contributorInfoData = CONTRIBUTOR_FIELDS.reduce((acc, field) => {
      acc[field] = (this as any)[field] || ''
      return acc
    }, {} as ContributorInfoData)
  }

  private saveToFlowCore() {
    const flowCore = MainModule.getInstance()?.getFlowManager()?.getCore(this.flowGroup)
    if (flowCore) {
      flowCore.save()
    } else {
      console.warn('FlowCore instance not found, saving aborted.')
    }
  }

  private getFieldValue(field: ContributorField): string {
    return (this as any)[field] || ''
  }

  private t(key: string): string {
    return Locale.getKey(`create-contributor-info.${key}`)
  }

  private toKebabCase(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Flow Interface Methods
  // ─────────────────────────────────────────────────────────────────────────

  public async serialize(): Promise<unknown> {
    return {
      ...this.contributorInfoData,
      postalAddressData: this.postalAddressData,
    }
  }

  public async deserialize(stepData: unknown): Promise<void> {
    const data =
      (stepData as ContributorInfoData & { postalAddressData?: ContributorAddress }) ?? {}

    // Populate all fields from saved data
    for (const field of CONTRIBUTOR_FIELDS) {
      ;(this as any)[field] = data[field] || ''
    }

    // Restore address data with defaults if not provided
    this.postalAddressData = {
      streetAddress: data.postalAddressData?.streetAddress || '',
      addressLine2: data.postalAddressData?.addressLine2 || '',
      addressLocality: data.postalAddressData?.addressLocality || '',
      addressRegion: data.postalAddressData?.addressRegion || '',
      postalCode: data.postalAddressData?.postalCode || '',
    }
    this.updateContributorInfoData()
    this.rerender(true)
  }

  public async validate(): Promise<boolean> {
    // Reset all errors
    for (const key in this.errors) {
      this.errors[key] = false
    }

    // Validate required fields
    const validations: { field: string; getValue: () => string }[] = [
      {
        field: 'nameFr',
        getValue: () => unescapeHtml((this.select('[name="nameFr"]') as HTMLInputElement)?.value),
      },
      {
        field: 'nameEn',
        getValue: () => unescapeHtml((this.select('[name="nameEn"]') as HTMLInputElement)?.value),
      },
      { field: 'contributorType', getValue: () => this.contributorType },
      {
        field: 'descriptionFr',
        getValue: () =>
          unescapeHtml((this.select('[name="descriptionFr"]') as HTMLInputElement)?.value),
      },
    ]

    for (const { field, getValue } of validations) {
      if (!getValue()) {
        this.errors[field] = this.t(`errors.${field}`)
      }
    }

    const hasErrors = Object.values(this.errors).some(Boolean)

    if (hasErrors) {
      window.scrollTo(0, 0)
      this.errors.generic = this.t('errors.generic')
    }

    return !hasErrors
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Template Helpers
  // ─────────────────────────────────────────────────────────────────────────

  private renderInput(
    field: ContributorField,
    options: {
      mt?: string
      placeholder?: string
      textarea?: boolean
      wordLimit?: number
      error?: string | false
    } = {}
  ): string {
    const { mt = '12px', placeholder, textarea = false, wordLimit, error } = options
    const placeholderText = placeholder ?? this.t('placeholder')
    const fieldError = error ?? this.errors[field] ?? ''

    return /*html*/ `
      <lvc-box mt="${mt}">
        <lvc-input
          secondary-style="true"
          ${textarea ? 'textarea="true"' : ''}
          on-change="${this.changeInputCb}"
          default-value="${this.getFieldValue(field)}"
          border="1px solid #1E007D"
          id="${this.toKebabCase(field)}"
          placeholder="${placeholderText}"
          name="${field}"
          ${fieldError ? `error="${fieldError}"` : ''}
          label="${this.t(field)}"
          ${wordLimit ? `wordLimit="${wordLimit}"` : ''}
        ></lvc-input>
      </lvc-box>
    `
  }

  private renderSectionTitle(titleKey: string): string {
    return /*html*/ `
      <lvc-box mt="24px">
        <lvc-text font-preset="titleMobileSmall" color="#1E007D">
          ${this.t(titleKey)}
        </lvc-text>
      </lvc-box>
    `
  }

  private renderPersonFields(): string {
    return /*html*/ `
      ${this.renderSectionTitle('personalInfo')}
      ${this.renderInput('nationality', { mt: '16px', placeholder: this.t('placeholderNationality') })}
      ${this.renderInput('birthplace', { placeholder: this.t('placeholderBirthplace') })}
      ${this.renderInput('workplace', { placeholder: this.t('placeholderWorkplace') })}
    `
  }

  private renderOrgFields(): string {
    const address = this.postalAddressData as ContributorAddress
    return /*html*/ `
      ${this.renderSectionTitle('orgInfo')}
      <lvc-box mt="16px">
        <lvc-mapbox-address-autofill
          on-change="${this.onChangeAddressAutocompCb}"
          place-address="${address.streetAddress || null}"
          place-address-line2="${address.addressLine2 || null}"
          place-city="${address.addressLocality || null}"
          place-province="${address.addressRegion || null}"
          place-zipcode="${address.postalCode || null}"
        ></lvc-mapbox-address-autofill>
      </lvc-box>
    `
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Template
  // ─────────────────────────────────────────────────────────────────────────

  html = () => {
    const isMobile = window.matchMedia('(max-width: 730px)').matches

    return /*html*/ `
      <div class="container">
        <lvc-box mt="24px" textAlign="left">
          <lvc-text font-preset="titleMobileSmall">
            ${this.t('title')}
          </lvc-text>
        </lvc-box>

        <lvc-box mt="24px" textAlign="left" color="#1E007D" font-preset="title">
          ${this.t('description')}
        </lvc-box>

        <lvc-box mt="12px">
          <lvc-error error="${this.errors.generic}"></lvc-error>
        </lvc-box>

        <lvc-box class="form">
          <lvc-box mt="8px" textAlign="${isMobile ? 'left' : 'center'}"></lvc-box>

          <!-- Name fields -->
          ${this.renderInput('nameFr', { mt: '24px', error: this.errors.nameFr })}
          ${this.renderInput('nameEn', { error: this.errors.nameEn })}

          <!-- Contributor type selector -->
          <lvc-box mt="12px">
            <lvc-contributor-type-field-v2
              name="contributorType"
              default-value="${this.contributorType ? encodeURIComponent(JSON.stringify([{ id: this.contributorType }])) : ''}"
              error="${this.errors.contributorType || ''}"
            ></lvc-contributor-type-field-v2>
          </lvc-box>

          <!-- Alternate name -->
          ${this.renderInput('alternateName', { mt: '16px' })}

          <!-- Description fields -->
          ${this.renderInput('descriptionFr', { mt: '16px', textarea: true, wordLimit: 250, error: this.errors.descriptionFr })}
          ${this.renderInput('descriptionEn', { textarea: true, wordLimit: 250 })}

          <!-- URL fields -->
          ${this.renderInput('mainEntityOfPageFr', { mt: '16px', placeholder: 'https://' })}
          ${this.renderInput('mainEntityOfPageEn', { placeholder: 'https://' })}

          <!-- Person-specific fields -->
          ${this.contributorType === 'Person' ? this.renderPersonFields() : ''}

          <!-- Organization-specific fields -->
          ${this.contributorType === 'Organization' ? this.renderOrgFields() : ''}

          <!-- Links and identifiers -->
          ${this.renderSectionTitle('linksAndIds')}
          ${this.renderInput('wikidataId', { mt: '16px' })}
          ${this.renderInput('isni')}
          ${this.renderInput('shopifyUrl', { placeholder: 'https://' })}
          ${this.renderInput('appleUrl', { placeholder: 'https://' })}
          ${this.renderInput('youtubeUrl', { placeholder: 'https://' })}
        </lvc-box>
      </div>
    `
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Styles
  // ─────────────────────────────────────────────────────────────────────────

  css = () => `
    :host {
      display: block;
    }

    .contributor-type-label {
      font-family: var(--font-family);
      color: #1E007D;
      font-size: 14px;
      font-weight: 700;
      display: block;
      margin-bottom: 8px;
    }

    select {
      width: 100%;
      height: 48px;
      font-family: var(--font-family);
      font-size: 14px;
      color: #1E007D;
      background: white;
      border: 1px solid #1E007D;
      border-radius: 99px;
      padding: 0 40px 0 16px;
      appearance: none;
      background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'><path d='M3.125 6.16626C4.99121 8.37417 7.04957 10.9183 9.28537 13.1965C9.6443 13.5622 10.2242 13.5863 10.6108 13.25C12.9689 11.199 14.6939 9.17938 16.875 6.36762' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>");
      background-repeat: no-repeat;
      background-position: right 15px top 50%;
      cursor: pointer;
    }

    select.has-value {
      background: #BFF0FF;
      border: 1px solid rgba(30, 0, 125, 0.3);
    }

    select.has-error {
      background: #FFEBD7;
      border-color: #FF4600;
    }

    select:focus-visible {
      outline: none;
    }

    select:focus {
      background: #BFF0FF url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'><path d='M16.875 13.8337C15.0088 11.6258 12.9504 9.08166 10.7146 6.80348C10.3557 6.43782 9.77576 6.41373 9.38917 6.75C7.03106 8.80105 5.30608 10.8206 3.125 13.6324' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat;
      background-position: right 15px top 50%;
    }

    option {
      font-family: var(--font-family);
      font-size: 14px;
      color: #1E007D;
    }
  `

  eventListeners: EventListeners = [
    [
      'lvc-contributor-type-field-v2',
      'change',
      e => this.onContributorChangeEvent(e as CustomEvent),
    ],
  ]
}
